import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { BsFillEyeFill, BsLightningFill } from 'react-icons/bs';
import { useFetchData } from 'services/queries';
import useAuth from "hooks/useAuth";
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import { STATUS_CONFIG } from 'constants';
import toast from 'react-hot-toast';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getContrastTextColor } from 'utils/colorUtils';
import TaxPrerequisitesDialog from 'components/dialogs/TaxPrerequisitesDialog';
import useTaxCalculation from 'hooks/useTaxCalculation';

const LoadingPlaceholder = () => (
  <Box sx={{ p: 2 }}>
    <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" height={400} />
  </Box>
);

const ComExOrdersList = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [searchText, setSearchText] = useState('');
    const [filteredOrders, setFilteredOrders] = useState([]);

    const { data: orders, error: errorOrders, isLoading: isLoadingOrders, refetch: refetchOrders } =
        useFetchData('order/simple-sales-order', true);

    const { auth, authLoading } = useAuth();
    
    // Use the tax calculation hook
    const {
        checkingOrderId,
        prerequisitesData,
        prerequisitesDialogOpen,
        isLoadingTaxCalcs,
        taxCalculations,
        handleCalculateTaxes,
        hasOrderTaxCalculation,
        closePrerequisitesDialog
    } = useTaxCalculation();

    // Filter orders based on search text
    useEffect(() => {
      if (orders) {
        if (!searchText) {
          setFilteredOrders(orders);
        } else {
          const lowercasedFilter = searchText.toLowerCase();
          const filtered = orders.filter(order => {
            return (
              order.order_identifier?.toLowerCase().includes(lowercasedFilter) ||
              order.customer_ref?.toLowerCase().includes(lowercasedFilter) ||
              order.buyer?.social_reason?.toLowerCase().includes(lowercasedFilter)
            );
          });
          setFilteredOrders(filtered);
        }
      }
    }, [orders, searchText]);

    const handleRefresh = () => {
        refetchOrders();
        toast.success("Data refreshed");
    };

    if (isLoadingOrders || isLoadingTaxCalcs || authLoading) {
        return <LoadingPlaceholder />;
    }

    if (errorOrders) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                  severity="error"
                  variant="filled"
                  sx={{
                    boxShadow: theme.shadows[4],
                    '& .MuiAlert-icon': {
                      fontSize: 28,
                      alignItems: 'center'
                    }
                  }}
                >
                    <AlertTitle>Error Loading Orders</AlertTitle>
                    {errorOrders?.message || "An unexpected error occurred."}
                </Alert>
            </Box>
        );
    }

    const columns = [
        {
            field: 'actions',
            headerName: 'Actions',
            width: isMobile ? 90 : 120,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            filterable: false,
            aggregable: false,
            groupable: false,
            renderCell: (params) => {
                const { id } = params.row;
                const hasTaxCalculation = hasOrderTaxCalculation(id);

                return (
                    <Stack 
                        direction="row" 
                        spacing={1} 
                        justifyContent="center" 
                        alignItems="center"
                        sx={{ height: '100%' }}
                    >
                        <Tooltip title="View Order Details" arrow>
                            <IconButton
                                size="small"
                                onClick={() => window.location.assign(`/admin/comex/order/detail/${id}`)}
                                color="primary"
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.2)
                                    }
                                }}
                            >
                                <BsFillEyeFill />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={checkingOrderId === id ? "Checking..." : "Calculate Taxes"} arrow>
                            <IconButton
                                size="small"
                                onClick={() => handleCalculateTaxes(id)}
                                color="warning"
                                disabled={checkingOrderId === id}
                                sx={{
                                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.warning.main, 0.2)
                                    }
                                }}
                            >
                                <BsLightningFill />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: 'order_identifier',
            headerName: 'Reference',
            width: isMobile ? 80 : 100,
            editable: false,
            aggregable: false,
            groupable: false
        },
        {
            field: 'customer_ref',
            headerName: 'Cust. Ref.',
            width: isMobile ? 80 : 100,
            editable: false,
            aggregable: false,
            groupable: false,
        },
        {
            field: 'social_reason',
            headerName: 'Customer',
            minWidth: 180,
            flex: 1,
            editable: false,
            aggregable: false,
            groupable: false,
        },
        {
            field: 'issue_date',
            headerName: 'Issue Date',
            type: 'date',
            width: isMobile ? 90 : 110,
            aggregable: false,
            groupable: false,
            valueGetter: (value, row) => {
                return row?.issue_date ? new Date(row.issue_date) : null;
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            aggregable: false,
            groupable: false,
            renderCell: (params) => {
                const status = params?.value;
                const config = STATUS_CONFIG[status] || {
                    color: '#BDC3C7', // Default gray color
                    icon: null
                };

                return (
                    <Chip
                        label={status}
                        size="small"
                        variant="filled"
                        sx={{
                            fontWeight: 500,
                            minWidth: 85,
                            backgroundColor: config.color,
                            color: getContrastTextColor(config.color)
                        }}
                    />
                );
            }
        },
        {
            field: 'tax_status',
            headerName: 'Tax Status',
            width: 130,
            aggregable: false,
            groupable: false,
            renderCell: (params) => {
                const hasTaxCalc = hasOrderTaxCalculation(params.row.id);
                return (
                    <Chip
                        icon={hasTaxCalc ? <CheckCircleIcon fontSize="small" /> : <ReportProblemIcon fontSize="small" />}
                        label={hasTaxCalc ? "Calculated" : "Pending"}
                        color={hasTaxCalc ? "success" : "default"}
                        size="small"
                        variant="filled"
                        sx={{ fontWeight: 500, minWidth: 110 }}
                    />
                );
            },
            valueGetter: (value, row) => {
                const hasTaxCalc = hasOrderTaxCalculation(row.id);
                return hasTaxCalc ? "Calculated" : "Pending";
            }
        },
    ];

    // Remove tax_status column on mobile devices
    if (isMobile) {
        columns.pop();
    }

    return (
        <>
            <Fade in timeout={500}>
                <Card
                    elevation={3}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                <CardContent sx={{ pb: 0, flexGrow: 0, bgcolor: theme.palette.background.paper }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="h5" component="h1" fontWeight="bold">
                            Orders
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            <TextField
                                placeholder="Search orders..."
                                size="small"
                                fullWidth={isMobile}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchText && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchText('')}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ minWidth: { sm: 200 } }}
                            />
                            <Tooltip title="Refresh Data">
                                <IconButton onClick={handleRefresh}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <Divider sx={{ mx: -2, mb: 2 }} />
                </CardContent>

                <Box sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <DataGridPremium
                        rows={filteredOrders}
                        columns={columns}
                        pagination
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            density: isMobile ? 'compact' : 'comfortable',
                            pinnedColumns: { left: ['actions'] },
                            pagination: { paginationModel: { pageSize: 25 } },
                            aggregation: {
                                model: {}  // Empty aggregation model
                            }
                        }}
                        disableAggregation
                        disableRowGrouping
                        autoHeight
                        disableRowSelectionOnClick
                        autosizeOptions={{
                            columns: isTablet ? ['order_identifier', 'customer_ref'] :
                                ['order_identifier', 'customer_ref', 'issue_date', 'status'],
                            includeOutliers: true,
                            includeHeaders: true,
                        }}
                        autosizeOnMount
                        sx={{
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 'bold',
                            },
                            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: theme => alpha(theme.palette.primary.light, 0.08),
                            },
                            border: 'none',
                            height: '100%',
                            flexGrow: 1,
                        }}
                    />
                </Box>
            </Card>
        </Fade>
        
        {/* Tax Prerequisites Dialog */}
        <TaxPrerequisitesDialog
            open={prerequisitesDialogOpen}
            onClose={closePrerequisitesDialog}
            prerequisites={prerequisitesData}
        />
    </>
    );
};

export default ComExOrdersList;
